import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Grid,
  Box,
  Chip,
} from '@mui/material';
import { Search, LocalHospital, Assignment } from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import UpcomingAppointmentsWidget from '../components/UpcomingAppointmentsWidget';
import { roleLabel } from '../utils/roleLabels';

export const HomePage: React.FC = () => {
  const { state } = useApp();
  const navigate = useNavigate();

  const getWelcomeMessage = () => {
    if (!state.user) return 'Welcome to Careva';
    
    const role = state.user.role;
    switch (role) {
      case 'PATIENT':
        return 'Welcome to your Care Dashboard';
      case 'CAREGIVER':
        return 'Welcome to your Caregiver Dashboard';
      case 'ADMIN':
        return 'Welcome to Admin Dashboard';
      default:
        return 'Welcome to Careva';
    }
  };

  const getRoleSpecificContent = () => {
    if (!state.user) return null;

    const role = state.user.role;
    switch (role) {
      case 'PATIENT':
        return (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Search sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Find Care
                </Typography>
                <Typography variant="body2" paragraph>
                  Search for qualified caretakers who can help with your healthcare needs.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={() => navigate('/search')}
                >
                  Search Caretakers
                </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      case 'CAREGIVER':
      case 'ADMIN':
        return (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Search sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Find Careseekers
                </Typography>
                <Typography variant="body2" paragraph>
                  Search for careseekers who need your care services and match your expertise.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  onClick={() => navigate('/search-patients')}
                >
                  Search Careseekers
                </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                  {getWelcomeMessage()}
                </Typography>
                {state.user?.role && (
                  <Chip
                    label={roleLabel(state.user.role)}
                    color="primary"
                    variant="outlined"
                    data-testid="home-role-chip"
                  />
                )}
              </Box>
              <Typography variant="body1" paragraph>
                {state.user?.role === 'PATIENT' 
                  ? 'Manage your healthcare journey and connect with qualified caretakers.'
                  : state.user?.role === 'CAREGIVER'
                  ? 'Provide exceptional care to your careseekers and manage your practice.'
                  : 'Monitor and manage the Careva platform.'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Appointments Widget - Full width */}
        <Grid item xs={12}>
          <UpcomingAppointmentsWidget />
        </Grid>

        {getRoleSpecificContent()}

        {/* Role-specific additional content */}
        {state.user?.role === 'PATIENT' && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <LocalHospital sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Healthcare Resources
                </Typography>
                <Typography variant="body2" paragraph>
                  Access healthcare information, emergency contacts, and care guidelines.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="outlined"
                  startIcon={<LocalHospital />}
                  onClick={() => navigate('/healthcare-resources')}
                >
                  View Resources
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}
        
        {state.user?.role === 'CAREGIVER' && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                  My Careseekers
                </Typography>
                <Typography variant="body2" paragraph>
                  Manage your current careseeker relationships and care plans.
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="outlined"
                  startIcon={<Assignment />}
                  onClick={() => navigate('/patient-management')}
                >
                  Manage Patients
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
